import { useEffect, useRef, useState } from "react"
import { useSearch } from "@tanstack/react-router"
import {
	IconLinkBrokenOutline18 as DisconnectIcon,
	IconLayersOutline18 as Layers,
	IconNodesOutline18 as Nodes,
	IconShieldCheckOutline18 as ShieldIcon,
} from "nucleo-ui-outline-18"
import { z } from "zod"

import { cn } from "@productlane/lib/src/cn"
import {
	AlertDialog,
	Badge,
	Button,
	Form,
	IconButton,
	IntegrationTemplate,
	Loader,
	useZodForm,
} from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { EllipsisIcon } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { GitHubIcon } from "@productlane/ui/src/icons/project/github-icon"
import { mutators } from "@productlane/zero/mutators"

import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { captureError } from "@/lib/capture-error"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { SettingItem } from "../settings-switches/setting-item"
import { SettingsContainer } from "../settings-switches/settings-container"
import { GithubRepositories } from "./github-repositories"
import { IntegrationLayout } from "./integration-layout"

const STUCK_THRESHOLD_MS = 5000

const linkSchema = z.object({
	repositoryFullName: z.string().min(1, "Pick a repository"),
})

const GITHUB_DESCRIPTION =
	"Link a repository and Productlane maps your product for the agent, writing an agent-only article for every screen your customers reach."

export const GITHUB_DOCS_DRAFTING_COPY =
	"Merged pull requests and commits to your default branch draft articles once a GitHub repository is connected. Released Linear issues work without it."

const GITHUB_LONG_DESCRIPTION = (
	<div>
		<div className="mb-8">
			<div className="mb-2 text-lg font-semibold text-primary">
				How the GitHub integration works
			</div>
			<div className="mb-6 text-sm leading-relaxed text-secondary">
				Productlane reads the repository you link once and writes an agent-only
				help center article for every screen your customers can reach, naming
				each button, field, and setting. Those articles are what the agent
				answers from. One repository is linked at a time.
			</div>
			<div className="text-sm leading-relaxed text-secondary">
				{GITHUB_DOCS_DRAFTING_COPY}
			</div>
		</div>

		<div className="mb-8 rounded-lg bg-gray-100 p-4 dark:bg-gray-850">
			<div className="mb-2 flex items-center gap-2 text-base font-semibold text-primary">
				<ShieldIcon className="h-4 w-4 flex-none text-tertiary" />
				The agent never reaches your code
			</div>
			<div className="mb-3 text-sm leading-relaxed text-secondary">
				The agent your customers talk to has no connection to GitHub. It cannot
				read a file, search your repository, or open a pull request. It answers
				from help center articles, and from nothing else.
			</div>
			<ul className="list-disc space-y-1.5 pl-5 text-sm text-secondary marker:text-tertiary">
				<li>
					<span className="text-primary">Productlane reads, never writes.</span>{" "}
					Every call we make to your repository is a read.
				</li>
				<li>
					<span className="text-primary">
						One repository, the one you pick.
					</span>{" "}
					Nothing else in your organization is reachable.
				</li>
				<li>
					<span className="text-primary">Customer-facing screens only.</span>{" "}
					Admin panels, internal dashboards, and staff tooling stay out of the
					map.
				</li>
				<li>
					<span className="text-primary">Secrets stay out.</span> A screen
					holding credentials, keys, or another customer&apos;s data is skipped,
					never summarized.
				</li>
			</ul>
		</div>

		<div className="mb-8">
			<div className="mb-2 text-base font-semibold text-primary">
				Setup instructions
			</div>
			<div className="mb-4 space-y-2 text-sm text-secondary">
				<div className="flex gap-2">
					<span className="text-primary">1.</span>
					<span>
						Click &quot;Connect&quot; to install the Productlane GitHub App on
						your organization.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">2.</span>
					<span>
						If your org requires admin approval, the request goes to an admin
						and this page connects once it is approved.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">3.</span>
					<span>
						Pick the repository to link. A single accessible repo is linked for
						you automatically.
					</span>
				</div>
			</div>
		</div>
	</div>
)

// A re-map costs real model spend, so the button reports the reason it is off
// rather than sitting greyed out with no explanation.
function RemapSettingItem({ hasRepo }: { hasRepo: boolean }) {
	const utils = api.useUtils()
	const statusQuery = api.repoIntegration.getRemapStatus.useQuery(undefined, {
		enabled: hasRepo,
	})
	const progressQuery = api.repoIntegration.getRemapProgress.useQuery(
		undefined,
		{ enabled: hasRepo },
	)
	const remap = api.repoIntegration.remapProduct.useMutation({
		onSuccess: () => {
			void utils.repoIntegration.getRemapStatus.invalidate()
			void utils.repoIntegration.getRemapProgress.invalidate()
		},
		onError: (error) => {
			toast.info({ title: error.message, icon: Layers })
		},
	})

	const blocked = statusQuery.data ? !statusQuery.data.canRemap : false
	const unavailable = !hasRepo || blocked

	return (
		<SettingItem
			className="pt-4"
			title="Map product for the agent"
			description={
				!hasRepo
					? "Add a repository to map your product."
					: statusQuery.data?.lastMappedAt
						? `Last mapped ${new Date(statusQuery.data.lastMappedAt).toLocaleDateString()}. Refreshes the agent-only articles.`
						: "Writes an agent-only article for every screen."
			}
			action={
				<div
					role="presentation"
					onClick={() => {
						if (!hasRepo) {
							toast.info({ title: "Add a repository first", icon: Layers })
							return
						}
						if (blocked) remap.mutate()
					}}
				>
					<Button
						variant="outline"
						Icon={Nodes}
						iconClassName="text-green-500"
						isLoading={
							remap.isLoading || progressQuery.data?.state === "running"
						}
						disabled={unavailable}
						// Matches the repository select one row up, down to the dropped
						// shadow, so the two controls in this card read as one set.
						className={cn(
							"h-8 min-h-8 rounded-md text-sm ring-inset dark:bg-gray-700/70 dark:text-gray-50 dark:shadow-none",
							unavailable && "pointer-events-none",
						)}
						onClick={() => remap.mutate()}
					>
						{statusQuery.data?.lastMappedAt ? "Re-map" : "Map now"}
					</Button>
				</div>
			}
		/>
	)
}

export function GithubIntegrationPage() {
	const isConfiguredQuery = api.repoIntegration.isConfigured.useQuery(
		undefined,
		{
			staleTime: Infinity,
		},
	)
	const workspace = useZeroWorkspace()
	const stuck = useStuckWatchdog(
		isConfiguredQuery.isLoading,
		isConfiguredQuery.status,
		isConfiguredQuery.isError,
	)

	if (isConfiguredQuery.isLoading || !workspace) {
		if (stuck) {
			return (
				<>
					<IntegrationLayout />
					<StuckBanner failed={false} />
				</>
			)
		}
		return (
			<>
				<IntegrationLayout />
				<div className="flex justify-center">
					<Loader />
				</div>
			</>
		)
	}

	if (isConfiguredQuery.isError) {
		return (
			<>
				<IntegrationLayout />
				<StuckBanner failed />
			</>
		)
	}

	return (
		<>
			<IntegrationLayout />
			<GithubIntegrationBody
				configured={isConfiguredQuery.data?.configured ?? false}
				settings={workspace.githubSettings ?? null}
			/>
		</>
	)
}

function useStuckWatchdog(
	isLoading: boolean,
	status: string,
	isError: boolean,
) {
	const [stuck, setStuck] = useState(false)
	const reported = useRef(false)

	useEffect(() => {
		if (!isLoading) {
			setStuck(false)
			return
		}
		const timeout = setTimeout(() => {
			setStuck(true)
			if (!reported.current) {
				reported.current = true
				analytics.track("github-page-stuck", { queryStatus: status })
			}
		}, STUCK_THRESHOLD_MS)
		return () => clearTimeout(timeout)
	}, [isLoading, status])

	useEffect(() => {
		if (!isError || reported.current) return
		reported.current = true
		captureError(new Error("Failed to load GitHub integration settings"), {
			domain: "integration",
			action: "load github integration settings",
			extra: { adblockerSuspected: true },
		})
		analytics.track("github-page-error")
	}, [isError])

	return stuck
}

function StuckBanner({ failed }: { failed: boolean }) {
	return (
		<div className="mx-auto max-w-[930px] px-6 py-10">
			<div className="rounded-xl border border-amber-200 bg-amber-50 p-5 dark:border-amber-800 dark:bg-amber-950">
				<div className="mb-2 text-sm font-medium text-amber-900 dark:text-amber-100">
					{failed
						? "This page failed to load."
						: "This page is taking longer than expected to load."}
				</div>
				<div className="mb-4 text-sm text-amber-800 dark:text-amber-200">
					A browser extension may be blocking a request needed for this page.
					Pausing content blockers for this site usually resolves it.
				</div>
				<Button onClick={() => window.location.reload()}>Reload page</Button>
			</div>
		</div>
	)
}

type GithubSettings = NonNullable<
	ReturnType<typeof useZeroWorkspace>
>["githubSettings"]

interface BodyProps {
	configured: boolean
	settings: GithubSettings | null
}

function GithubIntegrationBody({ configured, settings }: BodyProps) {
	const zero = useZero()
	const [dialogOpen, setDialogOpen] = useState(false)
	const callbackError = useSearch({ strict: false, select: (s) => s.error })
	const pending =
		useSearch({ strict: false, select: (s) => s.pending }) === "true"
	const [unlinking, setUnlinking] = useState(false)

	const installed = Boolean(settings)
	const currentRepo = settings?.repositoryFullName ?? null

	const installUrlQuery = api.repoIntegration.getInstallUrl.useQuery(
		undefined,
		{
			enabled: configured,
			staleTime: Infinity,
		},
	)

	const reposQuery = api.repoIntegration.listRepos.useQuery(undefined, {
		enabled: configured && installed,
		staleTime: 5 * 60_000,
		retry: 1,
	})

	const onLink = async (repositoryFullName: string) => {
		if (!settings) return
		if (repositoryFullName === currentRepo) return
		try {
			await mutateOk(
				zero.mutate(
					mutators.githubSettings.link({
						id: settings.id,
						repositoryFullName,
					}),
				),
			)
			analytics.track("github-link-repo")
			toast.success({ title: `Linked ${repositoryFullName}` })
		} catch (error) {
			toast.error({
				title: "Failed to link repository",
				description: (error as Error).message,
			})
		}
	}

	const uninstallMutation = api.repoIntegration.uninstall.useMutation()

	const onUnlink = async () => {
		if (!settings) return
		setUnlinking(true)
		try {
			await uninstallMutation.mutateAsync()
			analytics.track("github-disconnect")
			toast.success({ title: "GitHub uninstalled" })
		} catch (error) {
			toast.error({
				title: "Failed to disconnect",
				description: (error as Error).message,
			})
		} finally {
			setUnlinking(false)
		}
	}

	const linkForm = useZodForm({
		schema: linkSchema,
		defaultValues: { repositoryFullName: currentRepo ?? "" },
	})

	useEffect(() => {
		linkForm.reset({ repositoryFullName: currentRepo ?? "" })
	}, [currentRepo, linkForm])

	// Auto-link when the installation only grants access to a single repo —
	// saves the user a meaningless click. Keyed by settings.id so a fresh
	// install after uninstall re-arms the behavior.
	const autoLinkedForSettingsId = useRef<string | null>(null)
	useEffect(() => {
		if (!settings) {
			autoLinkedForSettingsId.current = null
			return
		}
		if (currentRepo) return
		if (autoLinkedForSettingsId.current === settings.id) return
		const repos = reposQuery.data
		if (!repos || repos.length !== 1) return
		autoLinkedForSettingsId.current = settings.id
		void onLink(repos[0]!.fullName)
		// onLink is fresh each render but the ref guard above makes that safe.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [settings, currentRepo, reposQuery.data])

	if (!configured) {
		return (
			<IntegrationTemplate
				logo={<GitHubIcon className="h-8 w-8" />}
				name={<div className="flex-1 font-medium">GitHub integration</div>}
				description={GITHUB_DESCRIPTION}
				button={null}
				instructions={
					<div className="rounded-xl border border-gray-200 bg-gray-50 p-5 dark:border-gray-700 dark:bg-gray-850">
						<div className="text-sm text-secondary">
							The GitHub App is not configured in this environment. A workspace
							admin needs to register a GitHub App and set
							<code className="mx-1">GITHUB_APP_ID</code>,
							<code className="mx-1">GITHUB_APP_PRIVATE_KEY</code>, and
							<code className="mx-1">GITHUB_APP_SLUG</code> before this
							integration can be enabled.
						</div>
					</div>
				}
			/>
		)
	}

	return (
		<>
			{callbackError ? (
				<div className="mx-auto mb-4 max-w-[930px] rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
					GitHub install failed ({callbackError}). Try again.
				</div>
			) : null}
			{pending ? (
				<div className="mx-auto mb-4 max-w-[930px] rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
					Install request sent to an org admin. This page will connect
					automatically once it&apos;s approved.
				</div>
			) : null}
			<IntegrationTemplate
				logo={<GitHubIcon className="h-8 w-8" />}
				name={
					<div className="flex flex-wrap items-center gap-2 font-medium">
						GitHub integration
						{installed ? (
							<Badge
								variant="green"
								size="sm"
								className="!text-green-700 dark:!text-green-400"
							>
								<span className="size-1.5 rounded-full bg-green-500" />
								Connected
							</Badge>
						) : null}
					</div>
				}
				description={GITHUB_DESCRIPTION}
				button={
					installed ? (
						// Disconnecting uninstalls the App for the whole org, so it sits in
						// the overflow rather than as the card's most prominent control.
						<DropdownMenu
							trigger={
								<IconButton
									Icon={EllipsisIcon}
									variant="dark-ghost"
									size="md"
									iconClassName="fill-current"
								/>
							}
							contentProps={{ side: "bottom", align: "end" }}
						>
							<DropdownMenuGroup>
								<DropdownMenuItem
									Icon={DisconnectIcon}
									isLoading={unlinking}
									disabled={unlinking}
									onClick={() => setDialogOpen(true)}
								>
									Disconnect
								</DropdownMenuItem>
							</DropdownMenuGroup>
						</DropdownMenu>
					) : (
						<Button
							onClick={() => {
								if (installUrlQuery.data?.url) {
									analytics.track("github-install-clicked")
									window.location.href = installUrlQuery.data.url
								}
							}}
							isLoading={installUrlQuery.isLoading}
							disabled={!installUrlQuery.data?.url}
						>
							Connect
						</Button>
					)
				}
				instructions={
					installed ? (
						<Form
							form={linkForm}
							onSubmit={() => {
								// autosave happens via FormSelect onChange; no explicit submit
							}}
							className="space-y-4"
						>
							<div className="text-sm font-medium">Repository</div>
							<SettingsContainer>
								{settings && (
									<GithubRepositories
										settingsId={settings.id}
										repositoryFullName={settings.repositoryFullName}
										additionalRepositories={settings.additionalRepositories}
										installationRepos={(reposQuery.data ?? []).map(
											(repo) => repo.fullName,
										)}
										loadingRepos={reposQuery.isLoading}
									/>
								)}
								<RemapSettingItem hasRepo={!!currentRepo} />
							</SettingsContainer>
							{reposQuery.isError && (
								<div className="text-xs text-red-500">
									Failed to load repositories. Please try again later.
								</div>
							)}
						</Form>
					) : (
						GITHUB_LONG_DESCRIPTION
					)
				}
			/>

			<AlertDialog
				variant="positive"
				isOpen={dialogOpen}
				onOpenChange={setDialogOpen}
				confirmText="Uninstall"
				title="Uninstall GitHub Integration?"
				description="This uninstalls the Productlane GitHub App from your organization and revokes its access to all repositories. To reconnect, you'll need to install the App again."
				onCancel={() => {
					setDialogOpen(false)
				}}
				onConfirm={() => {
					void onUnlink()
					setDialogOpen(false)
				}}
			/>
		</>
	)
}
